/**
 * Trip Export Modal Component
 *
 * Provides confirmation interface for exporting complete trip data
 * as ZIP archive with all related files and media
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Archive as ZipIcon,
  FileDownload as DownloadIcon,
  DateRange as CalendarIcon,
  LocationOn as LocationIcon,
  Group as TravelersIcon,
  Storage as StorageIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  TableChart as DataIcon,
  Description as DocumentIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Schedule as ClockIcon
} from '@mui/icons-material';

import tripExportService from '../../services/tripExportService';

const TripExportModal = ({ open, onClose, context, controller }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);
  const [exportProgress, setExportProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [tripSummary, setTripSummary] = useState(null);
  const [exportEstimate, setExportEstimate] = useState(null);
  const [exportComplete, setExportComplete] = useState(false);

  // Viewer-access guard: shared trips where the current user is only a viewer cannot be exported
  const isViewerOnly = context?.trip?.isShared && context?.trip?.sharedRole !== 'editor';

  const exportSteps = [
    'Gathering trip data...',
    'Processing itinerary information...',
    'Collecting accommodation details...',
    'Organizing travel bookings...',
    'Preparing document files...',
    'Creating export archive...',
    'Finalizing download...'
  ];

  useEffect(() => {
    if (open && context && !isViewerOnly) {
      loadTripSummary();
      generateExportEstimate();
    }
    if (!open) {
      resetState();
    }
  }, [open, context, isViewerOnly]);

  const resetState = () => {
    setIsExporting(false);
    setError(null);
    setExportProgress(0);
    setCurrentStep('');
    setExportComplete(false);
  };

  const loadTripSummary = async () => {
    try {
      const summary = await tripExportService.getTripSummary(context);
      setTripSummary(summary);
    } catch (error) {
      console.error('Failed to load trip summary:', error);
      setError('Failed to load trip information');
    }
  };

  const generateExportEstimate = async () => {
    try {
      const estimate = await tripExportService.getExportEstimate(context);
      setExportEstimate(estimate);
    } catch (error) {
      console.error('Failed to generate export estimate:', error);
    }
  };

  const simulateExportProgress = () => {
    let step = 0;
    const interval = setInterval(() => {
      if (step < exportSteps.length) {
        setCurrentStep(exportSteps[step]);
        setExportProgress(((step + 1) / exportSteps.length) * 90); // Leave 10% for final completion
        step++;
      } else {
        clearInterval(interval);
      }
    }, 800);

    return interval;
  };

  const handleExport = async () => {
    if (!context || !context.tripId) {
      setError('Invalid trip context. Please refresh and try again.');
      return;
    }

    setIsExporting(true);
    setError(null);
    setExportProgress(0);
    setExportComplete(false);

    const progressInterval = simulateExportProgress();

    try {
      // Use the controller if provided (from main-app), otherwise use service
      const exportMethod = controller ? controller.exportTrip.bind(controller) : tripExportService.exportCompleteTrip.bind(tripExportService);

      const success = await exportMethod(context, {
        includeDocuments: true,
        includeImages: true,
        exportFormat: 'zip'
      });

      clearInterval(progressInterval);

      if (success) {
        setExportProgress(100);
        setCurrentStep('Export completed successfully!');
        setExportComplete(true);

        // Auto-close after showing success
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setError('Export failed. Please try again.');
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error('Export failed:', error);
      setError(error.message || 'Export failed. Please try again.');
    } finally {
      setTimeout(() => setIsExporting(false), 1000);
    }
  };

  const handleClose = () => {
    if (!isExporting) {
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isExporting}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ZipIcon color="primary" />
          Export Complete Trip
        </Box>
      </DialogTitle>

      <DialogContent>
        {isViewerOnly ? (
          /* Viewer-only: permission denied message — no Firebase calls, no export UI */
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Export not available</strong><br />
              You have <strong>viewer</strong> access to this trip. Only the trip owner or users with
              editor access can export it. Contact the trip owner to request editor access.
            </Typography>
          </Alert>
        ) : (
          /* Owner / Editor: full export UI */
          <>
            {/* Trip Summary */}
            {tripSummary && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Trip Information
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                  <Chip
                    icon={<CalendarIcon />}
                    label={`${tripSummary.days} days`}
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    icon={<LocationIcon />}
                    label={tripSummary.destination}
                    variant="outlined"
                    size="small"
                  />
                  {tripSummary.travelers && (
                    <Chip
                      icon={<TravelersIcon />}
                      label={`${tripSummary.travelers} travelers`}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {exportEstimate && (
                    <Chip
                      icon={<StorageIcon />}
                      label={formatFileSize(exportEstimate.totalSize)}
                      variant="outlined"
                      size="small"
                    />
                  )}
                </Box>
                <Typography variant="body1" fontWeight="medium" gutterBottom>
                  {tripSummary.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tripSummary.dateRange}
                </Typography>
              </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Export Details */}
            <Typography variant="h6" gutterBottom>
              Export Details
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              This will create a complete ZIP archive containing all your trip data, documents, and media files.
              The archive can be used for backup or importing into another Trip Planner instance.
            </Alert>

            {/* Content Preview */}
            {exportEstimate && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1">
                    Archive Contents ({exportEstimate.totalItems} items)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <DataIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="Trip Data"
                        secondary={`${exportEstimate.dataFiles} JSON files with complete trip information`}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(exportEstimate.dataSize)}
                      </Typography>
                    </ListItem>

                    <ListItem>
                      <ListItemIcon>
                        <FileIcon color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary="CSV Exports"
                        secondary="Spreadsheet-compatible files for external use"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {formatFileSize(exportEstimate.csvSize)}
                      </Typography>
                    </ListItem>

                    {exportEstimate.documentCount > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <DocumentIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Documents (${exportEstimate.documentCount})`}
                          secondary="Booking confirmations, tickets, and other trip documents"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(exportEstimate.documentSize)}
                        </Typography>
                      </ListItem>
                    )}

                    {exportEstimate.mediaCount > 0 && (
                      <ListItem>
                        <ListItemIcon>
                          <ImageIcon color="success" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`Media Files (${exportEstimate.mediaCount})`}
                          secondary="Photos and images associated with your trip"
                        />
                        <Typography variant="body2" color="text.secondary">
                          {formatFileSize(exportEstimate.mediaSize)}
                        </Typography>
                      </ListItem>
                    )}
                  </List>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Export Progress */}
            {isExporting && (
              <Box mt={3} p={2} bgcolor="primary.50" borderRadius={1}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  {exportComplete ? (
                    <CheckIcon color="success" />
                  ) : (
                    <>
                      <CircularProgress size={20} />
                      <ClockIcon fontSize="small" color="primary" />
                    </>
                  )}
                  <Typography variant="body2" fontWeight="medium">
                    {currentStep}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={exportProgress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {Math.round(exportProgress)}% complete
                  {exportComplete && ' - Download should start automatically'}
                </Typography>
              </Box>
            )}

            {/* Error Message */}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {/* Success Message */}
            {exportComplete && (
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Export completed successfully!</strong><br />
                  Your complete trip archive has been downloaded. The ZIP file contains all your trip data,
                  documents, and can be used for backup or importing into another Trip Planner instance.
                </Typography>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          disabled={isExporting && !exportComplete}
          color="inherit"
        >
          {exportComplete ? 'Close' : 'Cancel'}
        </Button>
        {!isViewerOnly && (
          <Button
            onClick={handleExport}
            variant="contained"
            disabled={isExporting || !tripSummary || exportComplete}
            startIcon={
              isExporting ? (
                exportComplete ? <CheckIcon /> : <CircularProgress size={16} />
              ) : (
                <DownloadIcon />
              )
            }
          >
            {isExporting
              ? (exportComplete ? 'Complete' : 'Exporting...')
              : 'Create ZIP Export'
            }
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TripExportModal;